import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { datasourceEndpoints } from '@/api/endpoints';
import { callPut } from '@/api/fetcher';
import { Datasource, DatasourceType } from '@/shared/types/collections';

import { onDatasourcesDocumentsUpdated } from './useAPIDatasourcesDocuments';
import { useTabCategory } from './useCategory';
import { queryKeyFactories } from '../../../hooks/utils/queryKeyFactory';
import { DatasourceTab } from '../types';

/**
 * Hook parameters interface
 */
interface UseUpdateDatasourceDraftParams {
  collection_id: string;
}

type DatasourceConfig = Datasource['config'];

/**
 * API methods for the hook
 */
const API = Object.freeze({
  /**
   * Update the is_draft field for a datasource
   * This automatically bulk updates all documents in that datasource
   */
  updateDatasourceDraft: async (
    collection_id: string,
    datasource_id: string,
    type: DatasourceType,
    config: DatasourceConfig,
    is_draft: boolean
  ): Promise<Datasource> => {
    return callPut(datasourceEndpoints.datasource(collection_id, datasource_id), {
      type,
      config,
      is_draft,
    });
  },
});

const onDatasourceDraftUpdated = (
  queryClient: QueryClient,
  collection_id: string,
  datasource_id: string,
  category: DatasourceTab
) => {
  // Invalidate the folder documents query to refetch the updated documents
  queryClient.invalidateQueries({
    queryKey: queryKeyFactories.folderDocuments(collection_id, datasource_id),
  });

  // Update the datasources documents cache
  onDatasourcesDocumentsUpdated(queryClient, collection_id, category, datasource_id);

  // Invalidate the publish documents query to update the draft count
  queryClient.invalidateQueries({ queryKey: queryKeyFactories.publishDocuments(collection_id) });
};

/**
 * Custom React hook for bulk updating the is_draft field for all documents in a datasource
 *
 * This hook provides a way to publish or draft all documents in a datasource by updating
 * the datasource's is_draft field. The backend automatically bulk updates all documents.
 *
 * @param collection_id - The collection identifier
 *
 * @example
 * ```tsx
 * const { publishAllDocuments, draftAllDocuments } = useUpdateDatasourceDraft({
 *   collection_id: 'collection-123'
 * });
 *
 * // Publish all documents in a datasource
 * await publishAllDocuments({
 *   datasource_id: 'datasource-456',
 *   type: DatasourceType.FAQ,
 *   config: datasource.config
 * });
 *
 * // Draft all documents in a datasource
 * await draftAllDocuments({
 *   datasource_id: 'datasource-456',
 *   type: DatasourceType.FAQ,
 *   config: datasource.config
 * });
 * ```
 */
export const useUpdateDatasourceDraft = ({ collection_id }: UseUpdateDatasourceDraftParams) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { category } = useTabCategory();

  const { mutateAsync: publishAllDocuments, isPending: isPublishing } = useMutation({
    mutationFn: async ({
      datasource_id,
      type,
      config,
    }: {
      datasource_id: string;
      type: DatasourceType;
      config: DatasourceConfig;
    }) => {
      return API.updateDatasourceDraft(collection_id, datasource_id, type, config, false);
    },
    onSuccess: async (resp, { datasource_id }) => {
      onDatasourceDraftUpdated(queryClient, collection_id, datasource_id, category);

      toast.success(t('collections.publish_datasource_documents'));
    },
    meta: { errorToast: true },
  });

  const { mutateAsync: draftAllDocuments, isPending: isDrafting } = useMutation({
    mutationFn: async ({
      datasource_id,
      type,
      config,
    }: {
      datasource_id: string;
      type: DatasourceType;
      config: DatasourceConfig;
    }) => {
      return API.updateDatasourceDraft(collection_id, datasource_id, type, config, true);
    },
    onSuccess: async (resp, { datasource_id }) => {
      onDatasourceDraftUpdated(queryClient, collection_id, datasource_id, category);

      toast.success(t('collections.draft_datasource_documents'));
    },
    meta: { errorToast: true },
  });

  return { publishAllDocuments, draftAllDocuments, isPublishing, isDrafting };
};
